import React, { useMemo, useState, useEffect} from 'react';
import { useDropzone } from 'react-dropzone';
import { FiPaperclip, FiX } from "react-icons/fi";

export default function FileDropzone(props) {
  const [error, setError] = useState("");
  let projectFiles = props.projectData.files;
  let setProjectData = props.setProjectData;
  const [missingDescriptionsIndices, setMissingDescriptionsIndices] = useState([]);

  const { acceptedFiles, fileRejections, getRootProps, getInputProps, isFocused, open } = useDropzone({
    multiple: true,
    noClick: true,
    onDrop: (acceptedFiles) => {
      if (validateFiles(acceptedFiles)) {
        const extendedFiles = acceptedFiles.map(file => ({
          fileData: file,
          filedescription: '',
        }));
    
        setProjectData((prevState) => {
          return {
            ...prevState,
            files: [...prevState.files, ...extendedFiles]
          };
        });
      }
    }
    
  });

  // Displayed style when the file dropzone is selected
  const focusedStyle = {
    borderColor: '#2196f3'
  };
  const style = useMemo(() => ({
    ...(isFocused ? focusedStyle : {})
  }), [
    isFocused
  ]);

  /**
   * Removes a File object from the project files array based on index.
   * @param {*} index Index of the File object to be removed from the project files array
   */
  function deleteFile(index) {
    let duplicateFiles = projectFiles.slice(0);
    duplicateFiles.splice(index, 1);
    setProjectData((prevState) => ({
      ...prevState,
      files: duplicateFiles
    }));
  }

  /**
   * Validates arrays of File objects. Checks number of files, if files are inside a folder, file duplicates, and file size.
   * @param {*} acceptedFiles Array of file objects that were dropped into the file dropzone
   * @returns 
   */
  function validateFiles(acceptedFiles) {
    setError("");

    // Check if any of the accepted files are from folder selection
    // webkitRelativePath is only set when files are selected via folder selection (webkitdirectory)
    // This is the most reliable way to detect folder uploads
    if (acceptedFiles.some(file => file.webkitRelativePath && file.webkitRelativePath.split('/').length > 1)) {
      setError("Folder upload is not supported. Please zip the folder and try again.");
      return false;
    }

    // Check if there's more than 10 files uploaded (at once and in total)
    if (acceptedFiles.length > 10 || (projectFiles.length + acceptedFiles.length) > 10) {
      setError("The files you're trying to upload would exceed the 10 file limit.");
      return false;
    }

    // Check for duplicates
    const existingFileNames = projectFiles.map(file => file.name)
    const matchedFiles = acceptedFiles.filter(file => existingFileNames.includes(file.name))
    if (matchedFiles.length > 0) {
      setError("Cannot upload duplicate files.");
      return false;
    }

    // Check if there's more than 50 GB of files uploaded (at once and in total)
    // 20 GB is 21474836480 bytes
    // 50 GB is 53687091200 bytes
    // 100 GB is 107374182400 bytes
    const maxSize = 53687091200;
    if (getTotalFilesSize(acceptedFiles) > maxSize || getTotalFilesSize([...acceptedFiles, ...projectFiles]) > maxSize) {
      setError("The files you're trying to upload would exceed the 50GB limit.");
      return false;
    }

    return true;
  }

  /**
   * 
   * @param {*} filesArray Array of File objects
   * @returns Total size of all File objects in bytes
   */
  function getTotalFilesSize(filesArray) {
    if (filesArray.length > 0) {
      return filesArray.map(i => i.fileData ? i.fileData.size : 0).reduce((a, b) => a + b);
    }
  }

  /**
   * Converts a number of bytes to the appropriate size format and adds a suffix (KB, MB, GB, TB).
   * @param {*} bytes Number to be converted. Must be in bytes.
   * @returns Converted number
   */
  function getFileSize(bytes) {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  }

  function handleDescriptionChange(index, e) {
    const updatedFiles = [...projectFiles];
    updatedFiles[index].filedescription = e.target.value;
    setProjectData((prevState) => ({
      ...prevState,
      files: updatedFiles
    }));
  }
  function checkAllDescriptionsFilled() {
    const missingIndices = projectFiles.map((file, index) => {
        return (!file.filedescription || file.filedescription.trim() === '') ? index : null;
    }).filter(index => index !== null);
    setMissingDescriptionsIndices(missingIndices);
    return missingIndices.length === 0;
}

  /**
   * Displays the uploaded files in a styled unordered list
   * @returns Component listing the uploaded files
   */
  function renderUploadedFiles() {
    if (projectFiles.length > 0) {
      return (
        <>
          <ul role="list" className="mt-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-200 dark:divide-gray-600">
            {projectFiles.map((file, index) => (
              <li key={file.fileData.name} className="px-3 py-3 flex flex-col items-start justify-between text-sm">
                <div className="flex items-center justify-between w-full mb-2">
                  <div className="w-0 flex-1 flex items-center">
                    <FiPaperclip className="flex-shrink-0 h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                    <span className="ml-2 flex-1 w-0 truncate dark:text-gray-200">{file.fileData.name}</span>
                    <span className="ml-2 flex-1 w-0 truncate text-right dark:text-gray-200">{getFileSize(file.fileData.size)}</span>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <FiX onClick={() => deleteFile(index)} className="cursor-pointer flex-shrink-0 h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                </div>
                <div className="w-full relative mt-2">
                  <label
                    htmlFor={`filedescription-${index}`}
                    className="absolute -top-2 left-2 inline-block bg-white dark:bg-gray-800 px-1 text-xs font-medium text-gray-900 dark:text-gray-200"
                  >
                    Description
                  </label>
                  <input
                    maxLength="100"
                    type="text"
                    name={`filedescription-${index}`}
                    id={`filedescription-${index}`}
                    className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                    onChange={(e) => handleDescriptionChange(index, e)}
                    value={file.filedescription || ''}
                  />
                  {!file.filedescription && (
                    <p className="text-red-500 mt-2">Description is required.</p>
                  )}
                  {file.filedescription.length > 100 && (
                    <p className="text-red-500 mt-2">Description cannot exceed 100 characters.</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-1 mx-2 font-medium text-gray-600 dark:text-gray-400 text-right text-sm">{getFileSize(getTotalFilesSize(projectFiles))} / 50GB</p>
        </>
      )
    }
  }
  

  return (
    <section className="container">
      <div {...getRootProps({ style })} className="flex flex-1 flex-col items-center py-24 rounded-sm border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 outline-none">
        <input {...getInputProps()} />
        <p className="text-gray-500 dark:text-gray-400 text-base mb-1">Drag & drop files here</p>
        <p className="text-gray-500 dark:text-gray-400 text-sm">or</p>
        <button type="button" onClick={open} className="mt-2 flex justify-center py-2 px-6 border rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-600">
          Browse Files
        </button>
      </div>
      <div>
        <p className="mt-2 font-medium text-red-600 dark:text-red-500 mb-1 text-center text-sm">{error}</p>
        {renderUploadedFiles()}
        {/* Message about reaching the limit will show up at 50 GB (53687091200 bytes)*/}
        {getTotalFilesSize(projectFiles) >= 53687091200 &&
          <p className="mt-2 font-medium text-gray-600 dark:text-gray-400 mb-1 text-center">You've reached your 50GB limit. {"\u0020"}{/* space character */}
            {/* <a href="#" className="whitespace-nowrap font-medium text-blue-700 hover:text-blue-600">
              Request more space
              <span aria-hidden="true"> &rarr;</span>
            </a> */}
          </p>
        }
      </div>
    </section>
  );
}